import isEmpty from 'is-empty';
import React, { useEffect, useState } from 'react';
import ReactPixel from 'react-facebook-pixel';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { resetCartMutation, updateCustomerBookingMutation } from 'rsl-api-106/lib/mutations';
import {
    Cart, Mutation, MutationResetCartArgs, MutationUpdateCustomerBookingArgs
} from 'rsl-api-106/lib/types';

import { useApolloClient } from '@apollo/client';
import Button from '@components/_Layout/Controls/Button';
import InputLabel from '@components/_Layout/Controls/InputLabel';
import LinkButton from '@components/_Layout/Controls/LinkButton';
import Textarea from '@components/_Layout/Controls/Textarea';
import WidgetLayout from '@components/_Layout/WidgetLayout';
import {
    LVE1Tracking, TradedoublerTracking, UCatchItTracking, WIHPTracking
} from '@components/Tracking';
import GTMConfirmationPage from '@components/Tracking/GTMConfirmationPage';
import SelligentTrackingScript from '@components/Tracking/SelligentTrackingScript';
import { useCart, useConfig, useLoadingScreen, useSession } from '@hooks';
import dateFormat from '@utils/dateformat';
import { getAbsoluteImagePath } from '@utils/dom';
import { getLocalStorageItem } from '@utils/localStorage';
import { getCustomerUrlByWebUser } from '@utils/tools';

type ConfirmationPageProps = {};

const languagesVarsCodes = [
  "LANG_neobooking_go_to_area", //Accéder a mon espace perso
  "LANG_neobooking_add_reservation", //Ajouter une nouvelle réservation
  "LANG_neobooking_see_order_details", //Voir le détail de votre commande
  "LANG_neobooking_thanks_for_reservation", // Merci pour votre réservation
  "LANG_neobooking_confirmation_reservation_email_sent", //Un email de confirmation vous a été adressé à
  "LANG_neobooking_your_payment_of", //Votre paiement de
  "LANG_neobooking_confirmation_reservation_successfully_saved", //a bien été enregistré.
  "LANG_neobooking_my_reservation", //Ma réservation
  "LANG_neobooking_back_to_home", //Revenir à l'accueil
  "LANG_neobooking_back_to_website", //Revenir au site
  "LANG_neobooking_confirmation_reservation_confirm", //Votre réservation est confirmée !
  "LANG_neobooking_travel_with_tictactrip", // Voyager en transport durable avec Tictatrip
  "LANG_neobooking_tictactrip_informations", // Trouvez vos billets de train sncf, et réservez votre voyage en bus sur Tictatrip. Vous pouvez aussi retrouver les offres Blablacar pour un covoiturage par cher.
  "LANG_neobooking_reservation_saved_please_keep", //Veuillez conserver le numéro de dossier, il pourra vous être demandé ultérieurement.
  "LANG_neobooking_cart_save_num_res", //Votre numéro de dossier pour le séjour
  "LANG_neobooking_from_date", //du
  "LANG_neobooking_to", //au
  "LANG_neobooking_in_accomodation", //dans l'hébergement
  "LANG_neobooking_is", //est
  "LANG_neobooking_at", //à
  "LANG_neobooking_observation_remarques_successfully_updated", //Votre remarque à bien été ajoutée !
];

const ConfirmationPage: React.FC<ConfirmationPageProps> = () => {
  /* Hooks */
  const client = useApolloClient();
  const { authMode, getLanguageVarValue, sessionId } = useSession();
  const { cart, clear, loading, addObservation } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  const { mode: clientMode, logo: clientLogo } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const logo = clientLogo ? clientLogo : "Vacanceole-B-RVB.png";

  const handleSetReservationObservation = () => {
    const reservationId = searchParams.get("r");
    if (cart && cart.reservations && cart.reservations?.length > 0) {
      if (reservationId && !isNaN(parseInt(reservationId))) {
        const resa = cart?.reservations?.find(
          (x) => x?.id === parseInt(reservationId)
        );
        if (resa) {
          setObservation(resa.observation ?? "");
        }
      } else {
        const resa = cart?.reservations[0];
        if (resa) {
          setObservation(resa?.observation ?? "");
        }
      }
    }
  };

  /* Cart State */
  const [cartState, setCardState] = useState<Cart | null>(null);
  /* Observation State */
  const [observation, setObservation] = useState<string>("");
  const [loadingObservation, setLoadingObservation] = useState<boolean>(false);
  useEffect(() => {
    var _wic: any = _wic || [];
  }, []);

  useEffect(() => {
    if (!loading) {
      if (cart) {
        if (authMode !== 2) {
          handleSetReservationObservation();
        }
        const pspStatus = searchParams.get("s");
        if (pspStatus) {
          setCardState(cart);
          clear((err) => {});
        } else {
          window.location.href = window.location.origin;
        }
      }
    }
  }, [loading]);

  const roomInfosTitle = () => {
    let title = "";
    cartState?.reservations![0]?.stays?.forEach((stay, stayIndex) => {
      return (
        <div>
          {" "}
          {stay?.distribution?.roomTypes?.forEach((roomInfo, roomInfoIndex) => {
            title =
              title +
              `${stayIndex === 0 ? "" : "+ "} ${roomInfo?.quantity}x ${
                roomInfo?.label ?? ""
              } `;
          })}
        </div>
      );
    });

    return title;
  };

  const asyncAddReservationToCart = async () => {
    const bookingId = cartState?.reservations?.[0]?.id;
    if (bookingId) {
      const { data, errors } = await client.mutate<
        Mutation,
        MutationUpdateCustomerBookingArgs
      >({
        mutation: updateCustomerBookingMutation(),
        variables: {
          session: { name: sessionId },
          input: {
            bookingId: cartState?.reservations?.[0]?.id!!,
            fastCheckinCheckout: false,
          },
        },
      });
      return data?.updateCustomerBooking?.cart;
    } else {
      toast.error("Invalid bookingId");
      return undefined;
    }
  };

  const asyncClear = async () => {
    const { data, errors } = await client.mutate<
      Mutation,
      MutationResetCartArgs
    >({
      mutation: resetCartMutation(),
      variables: {
        session: { name: sessionId },
      },
    });

    return data?.resetCart?.cart;
  };

  const handleAddObservation = async () => {
    const updatedCart = await asyncAddReservationToCart();
    setLoadingObservation(true);
    if (updatedCart) {
      const cartItemsIds: number[] | null =
        updatedCart?.reservations?.map((x) => x?.cartItemID!!) ?? null;
      if (cartItemsIds) {
        addObservation(observation, cartItemsIds, async (err) => {
          if (err) toast.error(err.message);
          else {
            toast.success(
              getLanguageVarValue(
                "LANG_neobooking_observation_remarques_successfully_updated"
              )
            );
          }
          await asyncClear();
          setLoadingObservation(false);
        });
      } else {
        setLoadingObservation(false);
      }
    } else {
      setLoadingObservation(false);
    }
  };

  const getWebUser = () => {
    const localWebuser = getLocalStorageItem("webuser");
    return (
      searchParams.get("webuser") ??
      searchParams.get("wu") ??
      localWebuser ??
      process.env.REACT_APP_WEBUSER!!
    );
  };

  const handleGetRedirectionURL = () => {
    let url = process.env.REACT_APP_CUSTOMER_AREA_URL ?? window.location.origin;
    const webuser = getWebUser();
    if (authMode === 2) {
      if (mode === "corseole") {
        url =
          process.env.REACT_APP_CUSTOMER_URL_CORSEOLE ?? window.location.origin;
      } else {
        if (webuser === "webFR" || webuser === "web_fr_b2b") {
          url = `${process.env.REACT_APP_CUSTOMER_AREA_URL}?webuser=${webuser}&session=${sessionId}`;
        } else if (webuser === "webEN" || webuser === "web_en_b2b") {
          url = `${process.env.REACT_APP_CUSTOMER_AREA_EN_URL}?webuser=${webuser}&session=${sessionId}`;
        }
      }
    } else {
      //Si indiv, on fait un retour au site classique
      if (mode === "corseole") {
        url =
          process.env.REACT_APP_CUSTOMER_URL_CORSEOLE ?? window.location.origin;
      } else {
        if (webuser === "webEN") {
          url = `${process.env.REACT_APP_CUSTOMER_URL_EN}`;
        } else {
          url = `${process.env.REACT_APP_CUSTOMER_URL}`;
        }
      }
    }

    return url;
  };

  useLoadingScreen(loading);

  return (
    <WidgetLayout>
      <div className="tw-container tw-mx-auto tw-mt-16">
        <div
          className={`${
            mode === "vacanceole"
              ? "tw-bg-v_turquoise tw-text-v_blue-dark"
              : "tw-bg_corseole-secondary tw-text-white"
          } tw-pb-4 tw-pt-1 tw-px-8 md:tw-p-8 tw-flex tw-flex-col md:tw-flex-row tw-items-center`}
        >
          <a href={handleGetRedirectionURL()}>
            <img
              className="tw-w-[100px] md:tw-w-[170px] md:tw-mr-24 tw-mb-2 md:tw-mb-0 tw-self-start md:tw-self-center"
              src={getAbsoluteImagePath(`/assets/img/customer/${logo}`)}
            ></img>
          </a>
        </div>
        {cartState && !isEmpty(cartState.reservations) ? (
          <>
            {/* call tradedoubler & UCatchIt & lve1 tracking foreach reservation in cart */}
            {cartState.reservations?.map((resa) => {
              return resa ? (
                <>
                  <WIHPTracking resa={resa} />
                  <TradedoublerTracking reservation={resa} />
                  <UCatchItTracking reservation={resa} />
                  <LVE1Tracking reservation={resa} />
                </>
              ) : (
                <></>
              );
            })}
            <GooglePixelTracking cart={cartState} />
            <GTMConfirmationPage cart={cartState} />
            <GTagConfirmation cart={cartState} />
            <SelligentTrackingScript cartState={cartState} />
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 md:tw-items-center">
              <div className="tw-w-full">
                <div className="tw-text-center">
                  <img
                    className="tw-w-[60px] md:tw-w-[100px] tw-mx-auto tw-my-8"
                    src={getAbsoluteImagePath(
                      "/assets/img/icons/validation-circle.svg"
                    )}
                  ></img>
                  <p className="tw-titre-principal-dosis tw-mb-4 md:tw-mb-8">
                    {getLanguageVarValue(
                      "LANG_neobooking_confirmation_reservation_confirm"
                    )}{" "}
                  </p>
                </div>

                <p>
                  {getLanguageVarValue("LANG_neobooking_cart_save_num_res")}{" "}
                  <span className="tw-font-bold">
                    {cartState.reservations![0]?.property?.label}{" "}
                  </span>
                  {getLanguageVarValue("LANG_neobooking_at")}{" "}
                  <span className="tw-font-bold">
                    {cartState.reservations![0]?.property?.address?.city}
                  </span>{" "}
                  <span className="tw-lowercase">
                    {getLanguageVarValue("LANG_neobooking_from_date")}{" "}
                  </span>
                  <span className="tw-font-bold">
                    {dateFormat(
                      cartState.reservations![0]?.startDate!!,
                      "dd/mm/yy"
                    )}{" "}
                    {getLanguageVarValue("LANG_neobooking_to")}{" "}
                    {dateFormat(
                      cartState.reservations![0]?.endDate!!,
                      "dd/mm/yy"
                    )}{" "}
                  </span>
                  {getLanguageVarValue("LANG_neobooking_in_accomodation")}{" "}
                  <span>{roomInfosTitle()}</span>{" "}
                  {getLanguageVarValue("LANG_neobooking_is")}{" "}
                  <span
                    className={`tw-font-bold ${
                      mode === "vacanceole"
                        ? "tw-text-primary-500"
                        : "tw-text-primary_corseole-500"
                    } tw-text-xl`}
                  >
                    {cartState.reservations![0]?.id!!}
                  </span>
                  .
                </p>
                <p className="tw-text-sm tw-mt-2">
                  {getLanguageVarValue(
                    "LANG_neobooking_reservation_saved_please_keep"
                  )}
                </p>
                {authMode !== 2 ? (
                  <p className="tw-mt-6 tw-mb-6">
                    {getLanguageVarValue(
                      "LANG_neobooking_confirmation_reservation_email_sent"
                    )}{" "}
                    <span className="tw-font-bold">
                      {
                        cartState.reservations![0]?.primaryOccupant?.address
                          ?.mail
                      }
                    </span>
                  </p>
                ) : (
                  <p className="tw-mt-6 tw-mb-6">
                    {getLanguageVarValue(
                      "LANG_neobooking_confirmation_b2b_reservation"
                    )}
                  </p>
                )}
                <div className="tw-mb-6">
                  <LinkButton size="lg" href={handleGetRedirectionURL()}>
                    {getLanguageVarValue(
                      authMode === 2
                        ? "LANG_neobooking_goback_area_customer_b2b"
                        : "LANG_neobooking_back_to_website"
                    )}
                  </LinkButton>
                </div>
                {authMode !== 2 ? (
                  <>
                    <div className="tw-border-gray-300 tw-border-b tw-border-solid tw-w-[300px] tw-mb-4"></div>
                    <div className="tw-py-4">
                      <div>
                        <p className="tw-titre-principal-dosis tw-mb-4">
                          {getLanguageVarValue(
                            "LANG_neobooking_observation_remarques"
                          )}
                        </p>
                        <InputLabel truncate={false}>
                          {getLanguageVarValue(
                            "LANG_neobooking_observation_intro"
                          )}
                        </InputLabel>
                        <Textarea
                          onChange={(e) => setObservation(e.target.value)}
                          value={observation}
                          additionalClassname="tw-w-full"
                          rows={4}
                        />
                        <div className="tw-text-center">
                          <Button
                            additionalClassname="tw-bg-black tw-text-white tw-mt-4 tw-rounded-none tw-border-none tw-capitalize"
                            onClick={handleAddObservation}
                            isLoading={loadingObservation}
                          >
                            {getLanguageVarValue("LANG_neobooking_add")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
                {/* <p className='tw-mb-8'>
                                <SideOverlay title='Ma réservation'
                                    btnElement={(onClick) => <button type='button' onClick={onClick}
                                        className='tw-text-primary-500 hover:tw-text-primary-400 tw-underline tw-transition-colors tw-duration-200 tw-ease-in-out tw-underline-offset-4'>
                                        {getLanguageVarValue("LANG_neobooking_see_order_details")}</button>}>
                                    <BasketSummary border={false} enableEdit={false}
                                        reservations={cartState.reservations!!}></BasketSummary>
                                </SideOverlay>
                            </p> */}
                {/* <div className='tw-my-4 tw-flex tw-gap-2'>
                                <Button type="button">
                                    <NavLink to={routes.reservation}>
                                        {getLanguageVarValue("LANG_neobooking_add_reservation")}
                                    </NavLink>
                                </Button>
                                <Button type="button" shape="outline">
                                    <NavLink
                                        to={isOwner(authMode) ? routes.ownerArea : isPartner(authMode) ? routes.partnerArea : routes.customerArea}>
                                        {getLanguageVarValue("LANG_neobooking_go_to_area")}
                                    </NavLink>
                                </Button>
                            </div> */}
              </div>
            </div>
          </>
        ) : (
          /* empty state */
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2">
            <div className="tw-w-full sm:tw-w-2/3">
              <p className="title tw-mb-4">
                {getLanguageVarValue(
                  "LANG_neobooking_confirmation_reservation_confirm"
                )}
              </p>
              <LinkButton shape="outline" href={handleGetRedirectionURL()}>
                {getLanguageVarValue(
                  authMode === 2
                    ? "LANG_neobooking_goback_area_customer_b2b"
                    : "LANG_neobooking_back_to_website"
                )}
              </LinkButton>
            </div>
          </div>
        )}
        <div className="tw-border-gray-300 tw-border-b tw-border-solid tw-w-[300px] tw-my-8"></div>
        <div>
          <p className="tw-titre-principal-dosis tw-mb-4 md:tw-mb-8">
            {getLanguageVarValue("LANG_neobooking_travel_with_tictactrip")}
          </p>
          <p className="tw-mb-4">
            {getLanguageVarValue("LANG_neobooking_tictactrip_informations")}
          </p>
          <div id="ttt_wrapper">
            <TTTScript />
          </div>
        </div>
      </div>
    </WidgetLayout>
  );
};

export default ConfirmationPage;

type GTagProps = {
  cart: Cart;
};

const GTagConfirmation: React.FC<GTagProps> = ({ cart }) => {
  /* State */
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!loaded) return;
    initGTag();
  }, [loaded]);

  const initGTag = () => {
    /* Google Tag */
    window.dataLayer = window.dataLayer || [];
    gtag("js", new Date());
    gtag("config", "AW-668342220");
    gtag("config", "HA-75", {
      conversion_cookie_prefix: "_ha",
    });

    gtag("event", "purchase", {
      send_to: ["AW-668342220/ousXCPqDx8IBEMyv2L4C", "HA-75"],
      transaction_id: cart?.reservations!![0]?.id,
      value: cart?.reservations!![0]?.totalAmount?.amount?.toFixed(2),
      currency: cart?.reservations!![0]?.totalAmount?.currencyCode,
      items: cart?.reservations?.map((r) => {
        return {
          id: r?.property?.id,
          start_date: r?.startDate,
          end_date: r?.endDate,
        };
      }),
    });

    setLoaded(true);
  };

  return <></>;
};

const GooglePixelTracking: React.FC<GTagProps> = ({ cart }) => {
  useEffect(() => {
    /*Facebook Pixel Code Tracking*/
    ReactPixel.init(process.env.REACT_APP_GOOGLE_PIXEL_CODE!!);
    ReactPixel.track("Purchase", {
      value: cart?.reservations!![0]?.totalAmount?.amount?.toFixed(2),
      currency: cart?.reservations!![0]?.totalAmount?.currencyCode,
    });
  }, []);

  return <></>;
};

const TTTScript = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = "https://widgetbooking.tictactrip.eu/index.js";
    var s = document.getElementsByTagName("script")[0];
    s.parentNode?.insertBefore(script, s);
  }, []);

  return (
    <a
      href="https://www.tictactrip.eu"
      id="ttt-widget"
      data-iframe-partnerId="WB-HBT-VACANCEOLE"
    >
      {" "}
    </a>
  );
};
